/**
 * Thin, safe wrappers around the analytics pixels loaded in index.html:
 *  - Google Analytics (gtag.js)
 *  - TikTok Pixel (ttq)
 *
 * Every call is a no-op if the pixel didn't load (ad blockers, dev, consent
 * tools, etc.), so callers never need to guard.
 */
type Gtag = (...args: unknown[]) => void;
type Ttq = { track: (event: string, params?: Record<string, unknown>) => void; page: () => void };
type Fbq = (...args: unknown[]) => void;

function getGtag(): Gtag | null {
  const g = (window as unknown as { gtag?: Gtag }).gtag;
  return typeof g === 'function' ? g : null;
}

function getTtq(): Ttq | null {
  const t = (window as unknown as { ttq?: Ttq }).ttq;
  return t && typeof t.track === 'function' ? t : null;
}

function getFbq(): Fbq | null {
  const f = (window as unknown as { fbq?: Fbq }).fbq;
  return typeof f === 'function' ? f : null;
}

/**
 * Fire a "user registered" conversion on both GA and the TikTok pixel.
 * Call this exactly once, right after a successful sign-up.
 */
export function trackSignup(method: 'email' | 'google' = 'email', eventId?: string): void {
  try { getGtag()?.('event', 'sign_up', { method }); } catch { /* no-op */ }
  try { getTtq()?.track('CompleteRegistration', { method }); } catch { /* no-op */ }
  // `eventID` lets Meta dedupe this with the server-side CAPI event (`reg_<userId>`).
  try {
    getFbq()?.('track', 'CompleteRegistration', { method }, eventId ? { eventID: eventId } : undefined);
  } catch { /* no-op */ }
}

/**
 * Fire a "subscription started" conversion on GA, TikTok and Meta. Call this
 * once when a checkout completes and the subscription becomes trialing/active.
 * `value` is the recurring price (in the major unit, e.g. euros). For Meta's
 * `Subscribe` event we also send `predicted_ltv` (a ~1-year estimate), per
 * https://www.facebook.com/business/help/402791146561655.
 */
export function trackPurchase(opts: {
  value?: number;
  currency?: string;
  plan?: string;
  period?: 'monthly' | 'yearly';
  /** Shared with the server CAPI event (`sub_<subscriptionId>`) for Meta dedup. */
  eventId?: string;
}): void {
  const { value, currency = 'EUR', plan, period, eventId } = opts;
  // Predicted lifetime value ≈ one year of the chosen plan.
  const predictedLtv =
    value == null ? undefined : Math.round((period === 'yearly' ? value : value * 12) * 100) / 100;
  try {
    getGtag()?.('event', 'purchase', {
      currency,
      value,
      items: plan ? [{ item_name: plan, item_category: period }] : undefined,
    });
  } catch { /* no-op */ }
  try {
    getTtq()?.track('Subscribe', { content_name: plan, value, currency });
  } catch { /* no-op */ }
  try {
    getFbq()?.('track', 'Subscribe', { value, currency, predicted_ltv: predictedLtv, content_name: plan }, eventId ? { eventID: eventId } : undefined);
  } catch { /* no-op */ }
}

/**
 * Fire a "started checkout" conversion on GA (`begin_checkout`), TikTok and
 * Meta (`InitiateCheckout`). Call it when the checkout session exists, just
 * before sending the user to the payment page. `eventId` is shared with the
 * server CAPI event (`ic_<subscriptionId>`) so Meta counts it once.
 */
export function trackInitiateCheckout(opts: {
  value?: number;
  currency?: string;
  plan?: string;
  period?: 'monthly' | 'yearly';
  eventId?: string;
}): void {
  const { value, currency = 'EUR', plan, period, eventId } = opts;
  try {
    getGtag()?.('event', 'begin_checkout', {
      currency,
      value,
      items: plan ? [{ item_name: plan, item_category: period }] : undefined,
    });
  } catch { /* no-op */ }
  try {
    getTtq()?.track('InitiateCheckout', { content_name: plan, value, currency });
  } catch { /* no-op */ }
  try {
    getFbq()?.('track', 'InitiateCheckout', { value, currency, content_name: plan }, eventId ? { eventID: eventId } : undefined);
  } catch { /* no-op */ }
}

/**
 * Track a page view on the pixels that don't auto-track SPA route changes
 * (Meta + TikTok). GA4 handles history changes via Enhanced measurement, so we
 * skip gtag here to avoid double counting. Call on every client-side navigation
 * AFTER the initial load (index.html already fires the first PageView).
 */
export function trackPageView(): void {
  try { getFbq()?.('track', 'PageView'); } catch { /* no-op */ }
  try { getTtq()?.page(); } catch { /* no-op */ }
}
